const STORAGE_KEY = 'tim-massage-customer-session';
const CHANNEL_NAME = 'tim-massage-customer-auth';

export type CustomerAuthStatus = 'restoring' | 'authenticated' | 'refreshing' | 'authenticatedOffline' | 'signedOut';
export type CustomerAuthState = {
  status: CustomerAuthStatus;
  session: CustomerSession | null;
  profile: Record<string, unknown> | null;
};

export type CustomerSession = {
  accessToken: string;
  globalUserId?: string;
};

export const customerBrowserHeaders = {
  'X-Tim-Session-Mode': 'cookie',
  'X-Tim-Session-Scope': 'customer',
  'X-Tim-Tenant': 'massage',
  'X-Tim-Audience': 'tim-massage',
} as const;

let authState: CustomerAuthState = { status: 'restoring', session: null, profile: null };
const authListeners = new Set<(state: CustomerAuthState) => void>();
let authChannel: BroadcastChannel | null = null;

function emitAuthState(next: CustomerAuthState) {
  authState = next;
  for (const listener of authListeners) listener(authState);
}

function browserChannel() {
  if (authChannel || typeof BroadcastChannel === 'undefined') return authChannel;
  authChannel = new BroadcastChannel(CHANNEL_NAME);
  authChannel.addEventListener('message', (event) => {
    if (event.data?.type !== 'signed-out') return;
    writeCustomerSession(null, false);
    emitAuthState({ status: 'signedOut', session: null, profile: null });
  });
  return authChannel;
}

export function customerAuthSnapshot(): CustomerAuthState { return authState; }
export function subscribeCustomerAuth(listener: (state: CustomerAuthState) => void) {
  authListeners.add(listener);
  listener(authState);
  browserChannel();
  return () => authListeners.delete(listener);
}

export function readCustomerSession(): CustomerSession | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.accessToken !== 'string' || !parsed.accessToken) return null;
    return {
      accessToken: parsed.accessToken,
      globalUserId: typeof parsed.globalUserId === 'string' ? parsed.globalUserId : undefined,
    };
  } catch {
    return null;
  }
}

export function writeCustomerSession(session: CustomerSession | null, notifyPeers = false) {
  try {
    if (session?.accessToken) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Some embedded WebViews deny sessionStorage. The HttpOnly refresh cookie
    // still lets the current site restore a session on the next document load.
  }
  if (session?.accessToken) emitAuthState({ ...authState, status: 'authenticated', session });
  else emitAuthState({ status: 'signedOut', session: null, profile: null });
  if (notifyPeers && !session) browserChannel()?.postMessage({ type: 'signed-out' });
}

let refreshInFlight: Promise<CustomerSession | null> | null = null;
let refreshUnavailableUntil = 0;

async function performCustomerRefresh(): Promise<CustomerSession | null> {
  const previous = readCustomerSession();
  emitAuthState({ ...authState, status: 'refreshing', session: previous });
  try {
    const response = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...customerBrowserHeaders },
      body: '{}',
    });
    const envelope = await response.json().catch(() => null);
    if (!response.ok || typeof envelope?.data?.accessToken !== 'string') {
      if (response.status >= 500 || response.status === 429) {
        emitAuthState({ ...authState, status: previous ? 'authenticatedOffline' : 'restoring', session: previous });
        throw new Error('Dịch vụ đăng nhập tạm thời chưa phản hồi.');
      }
      refreshUnavailableUntil = Date.now() + 30_000;
      writeCustomerSession(null);
      return null;
    }
    refreshUnavailableUntil = 0;
    const session: CustomerSession = {
      accessToken: envelope.data.accessToken,
      globalUserId: typeof envelope.data.globalUserId === 'string' ? envelope.data.globalUserId : undefined,
    };
    writeCustomerSession(session);
    return session;
  } catch (error) {
    if (previous) emitAuthState({ ...authState, status: 'authenticatedOffline', session: previous });
    else emitAuthState({ status: 'signedOut', session: null, profile: null });
    throw error;
  }
}

export function refreshCustomerSession(): Promise<CustomerSession | null> {
  // A chat screen polls messages while other widgets restore the account.
  // The backend rotates refresh tokens, therefore only one refresh request may
  // be active or a later request can revoke the freshly rotated browser session.
  if (refreshInFlight) return refreshInFlight;
  if (Date.now() < refreshUnavailableUntil) return Promise.resolve(null);
  const current: Promise<CustomerSession | null> = ('locks' in navigator && navigator.locks)
    ? navigator.locks.request('tim-massage-customer-refresh', () => {
        return performCustomerRefresh();
      }) as unknown as Promise<CustomerSession | null>
    : performCustomerRefresh();
  const tracked = current.finally(() => {
    if (refreshInFlight === tracked) refreshInFlight = null;
  });
  refreshInFlight = tracked;
  return tracked;
}

export async function customerRequest(path: string, init: RequestInit = {}, retry = true): Promise<Response> {
  let session = readCustomerSession();
  if (!session && retry) session = await refreshCustomerSession();
  const headers = new Headers(init.headers || {});
  headers.set('X-Tim-Tenant', 'massage');
  headers.set('X-Tim-Audience', 'tim-massage');
  if (session?.accessToken) headers.set('Authorization', `Bearer ${session.accessToken}`);
  const response = await fetch(`/api${path}`, { ...init, credentials: 'same-origin', headers });
  if (response.status !== 401 || !retry) return response;
  const refreshed = await refreshCustomerSession();
  if (!refreshed) return response;
  headers.set('Authorization', `Bearer ${refreshed.accessToken}`);
  return fetch(`/api${path}`, { ...init, credentials: 'same-origin', headers });
}

export async function restoreCustomer(): Promise<Record<string, unknown> | null> {
  // Guest pages must not perform /me -> refresh -> /me a second time. First
  // resolve the optional cookie session once, then call /me only when a real
  // customer token exists.
  emitAuthState({ ...authState, status: 'restoring', session: readCustomerSession() });
  let session = readCustomerSession();
  if (!session) session = await refreshCustomerSession().catch(() => null);
  if (!session) { emitAuthState({ status: 'signedOut', session: null, profile: null }); return null; }
  const response = await customerRequest('/v1/me', {}, true);
  const envelope = await response.json().catch(() => null);
  if (!response.ok || !envelope?.data) {
    if (response.status === 401) writeCustomerSession(null);
    else emitAuthState({ ...authState, status: 'authenticatedOffline', session: readCustomerSession() });
    return null;
  }
  const current = readCustomerSession();
  if (current && typeof envelope.data.id === 'string') {
    writeCustomerSession({ ...current, globalUserId: envelope.data.id });
  }
  emitAuthState({ status: 'authenticated', session: readCustomerSession(), profile: envelope.data });
  return envelope.data;
}

export async function logoutCustomer(options: { allDevices?: boolean } = {}) {
  let session = readCustomerSession();
  if (!session) session = await refreshCustomerSession().catch(() => null);
  try {
    if (session) {
      await fetch(`/api/v1/auth/${options.allDevices ? 'revoke-all' : 'logout'}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          ...customerBrowserHeaders,
          Authorization: `Bearer ${session.accessToken}`,
        },
        body: '{}',
      });
    }
  } finally {
    refreshUnavailableUntil = Date.now() + 30_000;
    writeCustomerSession(null, true);
  }
}
